'use client'

import { Link, usePathname } from '@/shared/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  buildAttendanceSummaryFromRoster,
  mapStaffFromApi,
  mapAttendanceRecordsFromApi,
} from '@/features/kitchen/api/mapAttendanceFromApi'
import { buildProductionSummary } from '@/features/kitchen/api/buildProductionSummary'
import { buildDispatchSummaryFromShipments, mapDeliveriesFromApi } from '@/features/kitchen/api/mapDispatchFromApi'
import { fetchTodayAttendance } from '@/features/kitchen/api/getTodayAttendance'
import { fetchTodayDeliveries } from '@/features/kitchen/api/getTodayDispatch'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import {
  fetchTodayProductionPlan,
  fetchTodayProductionSessions,
} from '@/features/kitchen/api/getTodayProduction'
import { buildWasteSummaryFromBatches, mapWasteBatchesFromApi } from '@/features/kitchen/api/mapWasteFromApi'
import { fetchTodayWasteBatches } from '@/features/kitchen/api/getTodayWaste'
import { getKitchenHomeSummary, type HomeActionPriority } from '@/features/kitchen/storage/homeSummary'
import { todayDateKey } from '@/features/kitchen/storage/attendanceStorage'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { getKitchenSession } from '@/shared/auth/sessionStorage'
import { canPerformHeadActions } from '@/shared/auth/canPerformHeadActions'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonList } from '@/shared/components/Skeleton'

const MODULE_ICONS = {
  attendance: 'face_retouching_natural',
  production: 'skillet',
  dispatch: 'local_shipping',
  waste: 'delete_sweep',
} as const

type ApiLoadState = 'idle' | 'loading' | 'ready' | 'error'

function priorityLabel(priority: HomeActionPriority): string {
  return kitchenTerms.home.priority[priority]
}

function priorityBadgeClass(priority: HomeActionPriority): string {
  if (priority === 'high') return 'badge-danger'
  if (priority === 'medium') return 'badge-warning'
  return 'badge-draft'
}

export function KitchenHomePage() {
  const pathname = usePathname()
  const session = getKitchenSession()
  const device = getKitchenDevice()
  const [refreshKey, setRefreshKey] = useState(0)
  const [apiOverrides, setApiOverrides] = useState<Parameters<typeof getKitchenHomeSummary>[1]>(undefined)
  const [apiState, setApiState] = useState<ApiLoadState>('idle')
  const useApi = shouldUseKitchenApi()

  const summary = useMemo(
    () => getKitchenHomeSummary(undefined, apiOverrides),
    [pathname, refreshKey, apiOverrides],
  )

  const loadSummary = useCallback(() => {
    if (!useApi || !device?.deviceId) {
      setApiOverrides(undefined)
      setApiState('idle')
      return
    }

    let cancelled = false
    const date = todayDateKey()
    setApiState('loading')

    Promise.all([
      fetchTodayProductionPlan(device.deviceId),
      fetchTodayProductionSessions(device.deviceId),
      fetchTodayDeliveries(device.deviceId),
      fetchTodayWasteBatches(device.deviceId),
      fetchTodayAttendance(device.deviceId),
    ])
      .then(([plan, sessions, deliveries, waste, attendance]) => {
        if (cancelled) return
        const roster = mapStaffFromApi(attendance.staff)
        const records = mapAttendanceRecordsFromApi(attendance.staff, date)
        const dispatchShipments = mapDeliveriesFromApi(deliveries.deliveries)
        const wasteBatches = mapWasteBatchesFromApi(waste.batches)

        setApiOverrides({
          production: buildProductionSummary(plan, sessions.sessions),
          dispatch: buildDispatchSummaryFromShipments(dispatchShipments),
          waste: buildWasteSummaryFromBatches(wasteBatches),
          attendance: buildAttendanceSummaryFromRoster(roster, records),
        })
        setApiState('ready')
      })
      .catch(() => {
        if (!cancelled) {
          setApiOverrides(undefined)
          setApiState('error')
        }
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId])

  useEffect(() => {
    const cleanup = loadSummary()
    return cleanup
  }, [loadSummary, refreshKey])

  useEffect(() => {
    if (!useApi) return undefined
    const bump = () => setRefreshKey((k) => k + 1)
    const onVisible = () => {
      if (document.visibilityState === 'visible') bump()
    }
    window.addEventListener('focus', bump)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', bump)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [useApi])

  const todayLabel = new Intl.DateTimeFormat('id-ID', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date())

  const greeting = session?.userName
    ? kitchenTerms.home.greeting(session.userName)
    : kitchenTerms.home.greetingFallback

  const isInitialLoading = useApi && apiState === 'loading' && apiOverrides === undefined
  const isRefreshing = useApi && apiState === 'loading' && apiOverrides !== undefined

  const menus = [
    {
      to: '/kitchen/production',
      title: kitchenTerms.modules.production,
      subtitle: kitchenTerms.home.menus.production(
        summary.production.planPct,
        summary.production.openSessionCount,
      ),
      icon: 'skillet',
    },
    {
      to: '/kitchen/dispatch',
      title: kitchenTerms.modules.dispatch,
      subtitle: kitchenTerms.home.menus.dispatch(summary.dispatch.pendingCount),
      icon: 'local_shipping',
    },
    {
      to: '/kitchen/stock',
      title: kitchenTerms.modules.stock,
      subtitle: kitchenTerms.home.menus.stock,
      icon: 'inventory_2',
    },
    {
      to: '/kitchen/kitchen-transfer',
      title: kitchenTerms.modules.kitchenTransfer,
      subtitle: kitchenTerms.home.menus.kitchenTransfer,
      icon: 'swap_horiz',
    },
    ...(session && canPerformHeadActions(session)
      ? [
          {
            to: '/kitchen/warehouse-receive',
            title: kitchenTerms.modules.warehouseReceive,
            subtitle: kitchenTerms.home.menus.warehouseReceive,
            icon: 'move_to_inbox',
          },
          {
            to: '/kitchen/warehouse-return',
            title: kitchenTerms.modules.warehouseReturn,
            subtitle: kitchenTerms.home.menus.warehouseReturn,
            icon: 'undo',
          },
        ]
      : []),
    {
      to: '/kitchen/waste',
      title: kitchenTerms.modules.waste,
      subtitle: kitchenTerms.home.menus.waste(summary.waste.readyExecuteCount),
      icon: 'delete_sweep',
    },
    {
      to: '/kitchen/attendance',
      title: kitchenTerms.modules.attendance,
      subtitle: kitchenTerms.home.menus.attendance(
        summary.attendance.checkedIn,
        summary.attendance.total,
      ),
      icon: 'face_retouching_natural',
    },
  ] as const

  const statCards = [
    {
      to: '/kitchen/attendance',
      label: kitchenTerms.modules.attendance,
      value: `${summary.attendance.checkedIn}/${summary.attendance.total}`,
      unit: kitchenTerms.home.stats.attendance,
    },
    {
      to: '/kitchen/production',
      label: kitchenTerms.modules.production,
      value: `${summary.production.planPct}%`,
      unit: kitchenTerms.home.stats.production,
    },
    {
      to: '/kitchen/dispatch',
      label: kitchenTerms.modules.dispatch,
      value: String(summary.dispatch.pendingCount),
      unit: kitchenTerms.home.stats.dispatch,
    },
    {
      to: '/kitchen/waste',
      label: kitchenTerms.modules.waste,
      value: String(summary.waste.readyExecuteCount),
      unit: kitchenTerms.home.stats.waste,
    },
  ] as const

  return (
    <section className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-headline-lg-mobile text-primary break-words leading-snug">{greeting}</h1>
          <p className="text-body-sm text-on-surface-variant capitalize mt-0.5">
            {kitchenTerms.home.subtitle(device?.kitchenName ?? 'Dapur', todayLabel)}
          </p>
        </div>
        <button
          type="button"
          className="btn-outline shrink-0 min-w-[44px] px-3"
          onClick={() => setRefreshKey((value) => value + 1)}
          disabled={isRefreshing}
          aria-label={kitchenTerms.home.refreshLabel}
          title={isRefreshing ? kitchenTerms.home.refreshLoading : kitchenTerms.home.refreshLabel}
        >
          <span
            className={`material-symbols-outlined ${isRefreshing ? 'animate-spin' : ''}`}
          >
            refresh
          </span>
        </button>
      </header>

      {apiState === 'error' && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{kitchenTerms.home.loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((value) => value + 1)}
          >
            {kitchenTerms.home.retry}
          </button>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between gap-2 mb-3">
          <p className="text-body-lg font-bold text-on-surface">{kitchenTerms.home.actionsTitle}</p>
          {!isInitialLoading && summary.actions.length > 0 && (
            <span className="badge-draft">{kitchenTerms.home.actionsCount(summary.actions.length)}</span>
          )}
        </div>
        {isInitialLoading ? (
          <SkeletonList count={2} label={kitchenTerms.home.loadSummary} />
        ) : summary.actions.length === 0 ? (
          <EmptyState
            compact
            icon="task_alt"
            title={kitchenTerms.home.emptyTitle}
            description={kitchenTerms.home.emptyDescription}
          />
        ) : (
          <ul className="space-y-2">
            {summary.actions.map((action) => (
              <li key={action.id}>
                <Link
                  href={action.to}
                  className="flex items-start gap-3 rounded-lg border border-outline-variant p-3 hover:border-primary transition-colors"
                >
                  <span className="material-symbols-outlined text-primary mt-0.5">
                    {MODULE_ICONS[action.module]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-body-sm font-semibold text-on-surface break-words">{action.title}</p>
                      <span className={priorityBadgeClass(action.priority)}>
                        {priorityLabel(action.priority)}
                      </span>
                    </div>
                    <p className="text-body-sm text-on-surface-variant mt-0.5">{action.detail}</p>
                  </div>
                  <span className="material-symbols-outlined text-on-surface-variant">chevron_right</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="text-body-lg font-bold text-on-surface mb-3">{kitchenTerms.home.menuTitle}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {menus.map((item) => (
            <Link key={item.to} href={item.to} className="home-tile min-w-0">
              <span className="material-symbols-outlined text-primary text-[28px]">{item.icon}</span>
              <h2 className="text-body-lg font-bold text-on-surface leading-snug mt-2 break-words">{item.title}</h2>
              <p className="text-body-sm text-on-surface-variant mt-1 break-words">{item.subtitle}</p>
            </Link>
          ))}
        </div>
      </div>

      <div>
        <p className="text-body-lg font-bold text-on-surface mb-3">{kitchenTerms.home.statsTitle}</p>
        {isInitialLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="card p-3 space-y-2">
                <div className="skeleton h-3 w-16" />
                <div className="skeleton h-7 w-12" />
                <div className="skeleton h-3 w-20" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {statCards.map((stat) => (
              <Link
                key={stat.to}
                href={stat.to}
                className="card p-3 hover:border-primary transition-colors"
              >
                <p className="text-label-md text-on-surface-variant">{stat.label}</p>
                <p className="text-headline-md text-primary mt-1">{stat.value}</p>
                <p className="text-body-sm text-on-surface-variant mt-1">{stat.unit}</p>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-outline-variant/60 bg-surface-container/40 p-3 text-body-sm text-on-surface-variant space-y-1">
        <p>
          {kitchenTerms.roles.headKitchen}:{' '}
          <span className="font-semibold text-on-surface">{session?.userName ?? '—'}</span>
          {' · '}
          {device?.kitchenName ?? '—'}
        </p>
        <p className="text-body-sm">{kitchenTerms.home.sessionHint}</p>
      </div>
    </section>
  )
}
